/**
 * TEMPLATE FILTER & INDUSTRY NAVIGATION
 * ---------------------------------------------------------------
 * 1. Load dữ liệu sản phẩm:
    - Lấy toàn bộ product từ Shopify thông qua `/products.json`
    - Loại bỏ các template có tag `hypothesis`


 * 2. Xây dựng danh sách Industry:
    - Industry được tạo tự động dựa trên product tags
    - Mỗi tag của product được xem như một category/template industry
    - Loại bỏ product có tag trong: TemplateFilter.config.ignoreProducts
    - Không tạo Industry từ các tag trong: TemplateFilter.config.ignoreIndustryTags


 * 3. Hiển thị mặc định trên Homepage:
    - Khi chưa chọn industry (All templates):
        - Sắp xếp template theo độ ưu tiên (Template có tag Recommend)
        - Lấy một số template đại diện cho từng industry:
            + Số lượng industry được sử dụng:
                = Math.min(
                    Tổng số industry thực tế,
                    TemplateFilter.config.maxIndustries
                  )

            + Số lượng template hiển thị ban đầu:
                = Số industry được sử dụng
                  x TemplateFilter.config.productsPerIndustry

            Ví dụ:
                - Có 12 industry
                - maxIndustries = 100
                - productsPerIndustry = 2

                => All templates lấy: 12 x 2 = 24 templates

        - Loại bỏ template bị trùng lặp
        - Nếu chưa đủ số lượng sẽ lấy thêm template còn lại


 * 4. Lọc theo Industry:
    - Khi người dùng chọn một industry:
        - Chỉ hiển thị các template có tag tương ứng 
        - Giới hạn số lượng template hiển thị theo config: TemplateFilter.config.industryProductsLimit


 * 5. Sắp xếp template:
    - best-selling: Recommend > Trending > Featured > Template thường > Sau đó fallback theo ngày tạo mới nhất
    - created-descending: Template mới nhất trước
    - created-ascending: Template cũ nhất trước
    - title-ascending: Sắp xếp tên A → Z
    - title-descending: Sắp xếp tên Z → A


    const TemplateFilter = {
        { config  : Cấu hình giới hạn, selector, sorting  },
        { state   : Lưu dữ liệu runtime: products, industry hiện tại, danh sách industry  },
        { ui      : Xử lý render HTML  },
        { filter  : Xử lý lọc, sort và build industry  },
        { events  : Xử lý tương tác người dùng  },
        { api     : Xử lý gọi dữ liệu Shopify  },
        { helpers : các hàm hỗ trợ }
    }
*/

const TemplateFilter = {

    config: {
        industriesTarget: '#js-industries-list',
        productContainerTarget: '#js-product-list',
        sortBy: 'best-selling',

        maxIndustries: 20,        // Số lượng industry tối đa hiển thị
        productsPerIndustry: 1,    // Số template đại diện cho mỗi industry khi hiển thị All templates
        industryProductsLimit: 12, // Số template tối đa khi filter theo một industry

        ignoreProducts: new Set(['hypothesis']),    // loại bỏ product
        ignoreIndustryTags: new Set(['recommend']),  // không tạo industry (vd: ['recommend'])

        priority: {
            recommend: 3,
            trending: 2,
            featured: 1
        },

        // Sắp xếp page type, stt nhỏ lên trước
        PAGE_ORDER: {
            homepage: 0,
            'collection page': 1,
            'product page': 2,
            'blog page': 3,
            'blog post page': 4,
            'about page': 5,
            'faq page': 6,
            'contact page': 7
        }
    },

    state: {
        productList: [],
        activeIndustry: '',
        industries: [],
        industriesEl: null,
        productContainerEl: null
    },

    ui: {
        renderIndustryButtons() {
            const { state } = TemplateFilter;
            const { getIndustryIcon, escapeHtml } = TemplateFilter.helpers;

            const container = state.industriesEl;
            if (!container) return;

            const maxIndustries = TemplateFilter.config.maxIndustries;

            container.innerHTML = `
                <button
                    class="js-tpl-btn tlx__cat is-active"
                    data-type=""
                >
                    ${industriesIcon.all_templates}
                    <span> All templates </span>
                    <span class="tlx__cat-count"> ${state.productList.length} </span>
                </button>

                ${state.industries
                    .slice(0, maxIndustries)
                    .map(item => `
                        <button
                            class="js-tpl-btn tlx__cat"
                            data-type="${item.name}"
                        >
                            ${getIndustryIcon(item.name)}
                            <span>${escapeHtml(item.name)}</span>
                            <span class="tlx__cat-count">${item.count}</span>
                        </button>
                    `)
                    .join('')}
            `;
        },

        renderProductItem(product, imageWidth) {
            const { escapeHtml, getResizeImage } = TemplateFilter.helpers;

            const tagsHtml = product.__tags.map(tag => `
                <div class="tpl-card__badge ${tag === 'Recommend' ? '!order-[0]' : ''}">
                    ${tag === 'Recommend' ? TemplateIcon.recommendIcon : ''}
                    <span>${escapeHtml(tag)}</span>
                </div>
            `).join('');

            const image = product.images?.[0];
            const imageSrc = getResizeImage(image?.src, imageWidth);

            return `
                <article class="tpl-card">
                    <div class="tpl-card__thumb">
                        ${tagsHtml ? `<div class="tpl-card__tags">${tagsHtml}</div>` : ''}

                        <div class="w-full relative">
                            <img 
                                src="${imageSrc}" 
                                alt="${escapeHtml(image?.alt || product.title)}" 
                                width="${image?.width || ''}"
                                height="${image?.height || ''}"
                                loading="lazy"
                                decoding="async"
                                class="w-full h-full object-cover object-top m-auto block"
                            >
                        </div>

                        <div class="tpl-card__hover">
                            <a href="/pages/templates/${product.handle}" class="tpl-card__use">
                                <span>View Details</span>
                                ${TemplateIcon.arrowIcon}
                            </a>
                        </div>
                    </div>

                    <div class="tpl-card__body">
                        <a href="/pages/templates/${product.handle}"> 
                            <h3 class="tpl-card__title">${escapeHtml(product.title)}</h3>
                        </a>
                        
                        <div class="tpl-card__body-row">
                            <span>${escapeHtml(product.product_type || '')}</span>
                        </div>
                    </div>
                </article>
            `;
        },

        renderProducts(products) {
            const container = TemplateFilter.state.productContainerEl;
            if (!container) return;

            if (!products.length) {
                document.getElementById('js-homepage-template')?.remove();
                return;
            }

            // Đọc window.innerWidth 1 lần duy nhất cho cả danh sách,
            // thay vì mỗi item đọc lại (tránh forced reflow lặp lại).
            const imageWidth = window.innerWidth < 768 ? 640 : 400;

            container.innerHTML = `
                <div class="tpl-grid">
                    ${products.map(product => this.renderProductItem(product, imageWidth)).join('')}
                </div>
            `;
        },

        syncIndustryButtons() {
            const industriesTarget = TemplateFilter.state.industriesEl;
            if (!industriesTarget) return;

            const activeIndustry = TemplateFilter.state.activeIndustry;

            industriesTarget.querySelectorAll('.js-tpl-btn').forEach(btn => {
                const isActive = (btn.dataset.type || '') === activeIndustry;

                btn.classList.toggle('is-active', isActive);
                btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
            });
        },
    },

    filter: {
        apply() {

            const { config, state, ui } = TemplateFilter;

            let sortedProducts = this.sortProducts(
                this.getFilteredProducts(),
                config.sortBy
            );

            if (!state.activeIndustry) {

                const homepageProducts = [];
                const usedHandles = new Set();

                const industryLimit = Math.min(
                    state.industries.length,
                    config.maxIndustries
                );

                const maxProducts = industryLimit * config.productsPerIndustry;

                // Quét danh sách đã sort đúng 1 lần cho mỗi industry
                for (let i = 0; i < industryLimit; i++) {

                    const industry = state.industries[i].name;
                    let count = 0;

                    for (const product of sortedProducts) {

                        if (count >= config.productsPerIndustry) {
                            break;
                        }

                        if (usedHandles.has(product.handle)) {
                            continue;
                        }

                        if (!product.__tagSet.has(industry)) {
                            continue;
                        }

                        usedHandles.add(product.handle);
                        homepageProducts.push(product);
                        count++;

                    }

                }

                // Nếu chưa đủ thì lấy tiếp phần còn lại
                if (homepageProducts.length < maxProducts) {

                    for (const product of sortedProducts) {

                        if (homepageProducts.length >= maxProducts) {
                            break;
                        }

                        if (usedHandles.has(product.handle)) {
                            continue;
                        }

                        usedHandles.add(product.handle);
                        homepageProducts.push(product);
                    }
                }

                sortedProducts = homepageProducts;

            } else {

                sortedProducts = sortedProducts.slice(
                    0,
                    config.industryProductsLimit
                );

            }

            ui.renderProducts(sortedProducts);
            ui.syncIndustryButtons();

        },

        getFilteredProducts() {
            const { state } = TemplateFilter;

            const industryValue = state.activeIndustry;

            if (!industryValue) return state.productList;

            return state.productList.filter(product => {
                return product.__tagSet.has(industryValue);
            });
        },

        sortProducts(products, sortValue) {

            return [...products].sort((a, b) => {

                switch (sortValue) {

                    case 'best-selling':

                        return (
                            b.__priority - a.__priority ||
                            b.__created - a.__created
                        );

                    case 'created-descending':

                        return b.__created - a.__created;

                    case 'created-ascending':

                        return a.__created - b.__created;

                    case 'title-ascending':

                        return (a.title || '')
                            .localeCompare(b.title || '');

                    case 'title-descending':

                        return (b.title || '')
                            .localeCompare(a.title || '');

                    default:

                        return 0;

                }

            });

        },

        setIndustry(industry) {
            TemplateFilter.state.activeIndustry = industry || '';
            this.apply();
        },

        buildIndustries() {
            const { config, state } = TemplateFilter;
            const countMap = Object.create(null);

            for (const product of state.productList) {

                for (const tag of product.__tagSet) {

                    if (config.ignoreIndustryTags.has(tag.toLowerCase())) {
                        continue;
                    }

                    countMap[tag] = (countMap[tag] || 0) + 1;
                }
            }


            state.industries = Object.entries(countMap)
                .map(([name, count]) => ({ name, count }))
                .sort((a, b) => {
                    const orderA = config.PAGE_ORDER[a.name.toLowerCase()];
                    const orderB = config.PAGE_ORDER[b.name.toLowerCase()];

                    if (orderA !== undefined && orderB !== undefined) {
                        return orderA - orderB;
                    }

                    if (orderA !== undefined) return -1;
                    if (orderB !== undefined) return 1;

                    return b.count - a.count;
                });
        }
    },

    events: {
        init() {
            this.industryChange();
        },

        industryChange() {
            const industriesTarget = TemplateFilter.state.industriesEl;
            if (!industriesTarget) return;

            industriesTarget.addEventListener('click', (e) => {
                const btn = e.target.closest('.js-tpl-btn');
                if (!btn) return;

                TemplateFilter.filter.setIndustry(btn.dataset.type || '');
            });
        }
    },

    api: {
        CACHE_KEY: 'tpl_filter_products_cache_v1',
        CACHE_TTL: 60 * 60 * 1000,

        async fetchAllProducts() {
            const { readCache, writeCache } = TemplateFilter.helpers;

            const cached = readCache(this.CACHE_KEY, this.CACHE_TTL);
            if (cached) return cached;

            try {
                const products = await this.loadProducts();
                const filtered = this.filterProducts(products);

                writeCache(this.CACHE_KEY, filtered);

                return filtered;

            } catch (error) {
                console.error('Lỗi khi tải danh sách sản phẩm:', error);
                return [];
            }
        },

        async loadProducts() {
            const LIMIT = 250;

            let page = 1;
            const products = [];

            while (true) {

                const res = await fetch(
                    `${window.location.origin}/products.json?limit=${LIMIT}&page=${page}`
                );

                if (!res.ok) {
                    throw new Error(`HTTP ${res.status}`);
                }

                const data = await res.json();

                if (!data.products.length) {
                    break;
                }

                products.push(...data.products);

                if (data.products.length < LIMIT) {
                    break;
                }

                page++;
            }

            return products;
        },

        filterProducts(products) {
            const ignoreProducts = TemplateFilter.config.ignoreProducts;

            return products.filter(product =>
                !(product.tags || []).some(tag =>
                    ignoreProducts.has(tag.toLowerCase())
                )
            );
        }
    },

    helpers: {
        readCache(key, ttl) {
            try {
                const raw = sessionStorage.getItem(key);
                if (!raw) return null;

                const { data, timestamp } = JSON.parse(raw);

                if (!data || Date.now() - timestamp > ttl) {
                    sessionStorage.removeItem(key);
                    return null;
                }

                return data;
            } catch (error) {
                return null;
            }
        },

        writeCache(key, data) {
            try {
                sessionStorage.setItem(key, JSON.stringify({
                    data,
                    timestamp: Date.now()
                }));
            } catch (error) {
                // sessionStorage unavailable hoặc quota exceeded
            }
        },

        getIndustryIcon(name) {
            const key = name.toLowerCase().trim().replace(/\s+/g, '_');
            return industriesIcon[key] || industriesIcon.default_icon;
        },

        getResizeImage(url, width = 640) {
            if (!url) return '';

            const separator = url.includes('?') ? '&' : '?';
            return `${url}${separator}width=${width}`;
        },

        escapeHtml(str) {
            return String(str ?? '').replace(/[&<>"']/g, (c) => ({
                '&': '&amp;',
                '<': '&lt;',
                '>': '&gt;',
                '"': '&quot;',
                "'": '&#39;'
            }[c]));
        },
    },

    async init() {

        // Cache DOM node 1 lần, dùng lại cho mọi lần render.
        this.state.industriesEl = document.querySelector(this.config.industriesTarget);
        this.state.productContainerEl = document.querySelector(this.config.productContainerTarget);

        this.state.productList = await this.api.fetchAllProducts();

        const priorityMap = this.config.priority;

        this.state.productList.forEach(product => {

            let priority = 0;
            const tags = product.tags || [];
            product.__tags = tags;
            product.__tagSet = new Set(tags);

            for (const tag of tags) {

                const value = priorityMap[tag.toLowerCase()] || 0;

                if (value > priority) {
                    priority = value;
                }
            }

            product.__priority = priority;
            product.__created = product.created_at ? Date.parse(product.created_at) : 0;

        });

        this.filter.buildIndustries();

        document.querySelectorAll('.js-template-count')
            .forEach(el => el.textContent = this.state.productList.length);

        this.ui.renderIndustryButtons();

        this.events.init();
        this.filter.apply();
    }
};

function initHomepageTemplateObserver() {
    const section = document.querySelector('#js-homepage-template');
    if (!section) return;

    let hasInited = false;

    const observer = new IntersectionObserver(
        async (entries, obs) => {
            const entry = entries[0];
            if (!entry?.isIntersecting || hasInited) return;

            hasInited = true;
            await TemplateFilter.init();
            obs.unobserve(section);
        },
        {
            threshold: 0,
            rootMargin: '450px 0px'
        }
    );

    observer.observe(section);
}

document.addEventListener('DOMContentLoaded', initHomepageTemplateObserver);